import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Heart, Share2 } from 'lucide-react';
import { useEditMode } from '../../hooks/experiences/useEditMode';
import { useSaveSectionData } from '../../hooks/experiences/useSectionData';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3030';


/**
 * ImageGallery Component - View Mode
 * Image gallery with navigation, like, and share functionality
 * Now includes title and tagline overlay from Hero Section data
 */
function ImageGalleryView({ images = [], alt = 'Guide experience', title, tagline, isGuide = false, onEdit }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isLiked, setIsLiked] = useState(false);

  // Use default placeholder if no images (base64-encoded SVG placeholder, not a secret)
  // eslint-disable-next-line no-secrets/no-secrets
  const defaultPlaceholder = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwMCIgaGVpZ2h0PSI1MDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjEyMDAiIGhlaWdodD0iNTAwIiBmaWxsPSIjZTVlN2ViIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIyNCIgZmlsbD0iIzlhYTBhNiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkFkZCBIZXJvIEltYWdlPC90ZXh0Pjwvc3ZnPg==';
  const displayImages = (!images || images.length === 0) ? [defaultPlaceholder] : images;
  const hasMultipleImages = images && images.length > 1;

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev === 0 ? displayImages.length - 1 : prev - 1));
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  return (
    <div className="mb-6">
      <div className="relative rounded-xl overflow-hidden shadow-lg">
        <img
          src={displayImages[currentImageIndex]}
          alt={alt}
          className="w-full h-96 object-cover"
        />

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 to-black/60" />

        {/* Title and Tagline Overlay */}
        {(title || tagline) && (
          <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center text-white z-10">
            {title && (
              <h1 className="text-5xl font-bold mb-4 max-w-4xl">
                {title}
              </h1>
            )}
            {tagline && (
              <p className="text-xl mb-8 max-w-2xl">
                {tagline}
              </p>
            )}
          </div>
        )}

        {hasMultipleImages && (
          <>
            <button
              onClick={prevImage}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg transition z-20"
            >
              <ChevronLeft className="w-5 h-5 text-gray-700" />
            </button>

            <button
              onClick={nextImage}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg transition z-20"
            >
              <ChevronRight className="w-5 h-5 text-gray-700" />
            </button>
          </>
        )}

        {/* Edit Hero Button (Guide Only) - Centered at top */}
        {isGuide && onEdit && (
          <button
            onClick={onEdit}
            className="absolute top-4 left-1/2 -translate-x-1/2 z-30 bg-white text-gray-800 px-4 py-2 rounded-lg shadow-lg hover:bg-gray-100 transition-colors"
          >
            Edit Hero
          </button>
        )}

        <div className="absolute top-4 right-4 flex gap-2 z-20">
          <button
            onClick={() => setIsLiked(!isLiked)}
            className="bg-white/90 hover:bg-white p-2 rounded-full shadow-lg transition"
          >
            <Heart className={isLiked ? 'w-5 h-5 fill-red-500 text-red-500' : 'w-5 h-5 text-gray-700'} />
          </button>
          <button className="bg-white/90 hover:bg-white p-2 rounded-full shadow-lg transition">
            <Share2 className="w-5 h-5 text-gray-700" />
          </button>
        </div>
      </div>

      {hasMultipleImages && (
        <div className="flex gap-2 p-2 bg-white overflow-x-auto">
          {images.map((img, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentImageIndex(idx)}
              className={`flex-shrink-0 w-16 h-16 rounded overflow-hidden border-2 transition ${
                idx === currentImageIndex ? 'border-rose-500' : 'border-transparent'
              }`}
            >
              <img src={img} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * ImageGallery Component - Edit Mode
 * Edit form for hero section data
 */
function ImageGalleryEdit({
  data,
  updateField,
  onCancel,
  onSaveDraft,
  onPublish,
  isSaving,
  hasUnsavedChanges,
  error,
  title,
  onTitleChange
}) {
  const { tagline, heroImage, ctaText, ctaLink } = data;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
      <h2 className="text-2xl font-bold mb-6">Edit Hero Section</h2>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <div className="space-y-6">
        {/* Title */}
        <div>
          <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
            Experience Title <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="title"
            name="title"
            value={title || ''}
            onChange={(e) => onTitleChange(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Enter experience title (10-100 characters)"
            minLength={10}
            maxLength={100}
            required
          />
          <p className="text-sm text-gray-500 mt-1">
            {title?.length || 0}/100 characters
          </p>
          <p className="text-sm text-gray-600 mt-1">
            This updates the main experience title (experiences.title)
          </p>
        </div>

        {/* Tagline */}
        <div>
          <label htmlFor="tagline" className="block text-sm font-medium text-gray-700 mb-2">
            Tagline <span className="text-red-500">*</span>
          </label>
          <textarea
            id="tagline"
            name="tagline"
            value={tagline || ''}
            onChange={(e) => updateField('tagline', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Enter compelling tagline (20-200 characters)"
            rows={3}
            minLength={20}
            maxLength={200}
            required
          />
          <p className="text-sm text-gray-500 mt-1">
            {tagline?.length || 0}/200 characters
          </p>
        </div>

        {/* Hero Image URL */}
        <div>
          <label htmlFor="heroImage" className="block text-sm font-medium text-gray-700 mb-2">
            Hero Image URL <span className="text-red-500">*</span>
          </label>
          <input
            type="url"
            id="heroImage"
            name="heroImage"
            value={heroImage || ''}
            onChange={(e) => updateField('heroImage', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="https://example.com/image.jpg"
            required
          />
          {heroImage && (
            <div className="mt-2">
              <img
                src={heroImage}
                alt="Preview"
                className="w-full h-48 object-cover rounded-lg"
                onError={(  _e) => {
                  _e.target.style.display = 'none';
                }}
              />
            </div>
          )}
        </div>

        {/* CTA Text (Optional) */}
        <div>
          <label htmlFor="ctaText" className="block text-sm font-medium text-gray-700 mb-2">
            CTA Button Text (Optional)
          </label>
          <input
            type="text"
            id="ctaText"
            name="ctaText"
            value={ctaText || ''}
            onChange={(e) => updateField('ctaText', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Book Now"
            maxLength={50}
          />
        </div>

        {/* CTA Link (Optional) */}
        <div>
          <label htmlFor="ctaLink" className="block text-sm font-medium text-gray-700 mb-2">
            CTA Button Link (Optional)
          </label>
          <input
            type="url"
            id="ctaLink"
            value={ctaLink || ''}
            onChange={(e) => updateField('ctaLink', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="https://example.com/book"
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
        <button
          onClick={onCancel}
          disabled={isSaving}
          className="px-6 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
        >
          Cancel
        </button>

        <div className="flex gap-3">
          <button
            onClick={onSaveDraft}
            disabled={isSaving || !hasUnsavedChanges}
            className="px-6 py-2 text-gray-700 bg-yellow-100 hover:bg-yellow-200 rounded-lg transition-colors disabled:opacity-50"
          >
            {isSaving ? 'Saving...' : 'Save Draft'}
          </button>
          <button
            onClick={onPublish}
            disabled={isSaving || !hasUnsavedChanges}
            className="px-6 py-2 text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors disabled:opacity-50"
          >
            {isSaving ? 'Publishing...' : 'Save & Publish'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Main ImageGallery Component with Edit Mode Support
 */
export default function ImageGallery({
  images = [],
  alt = 'Guide experience',
  title,
  tagline,
  isGuide = false,
  experienceId,
  sectionTypeId: _sectionTypeId,
  heroData,
  onSectionSaved
}) {
  const { save, saving: _saving, error: saveError } = useSaveSectionData(experienceId, _sectionTypeId);
  const [localError, setLocalError] = useState(null);
  const [experienceTitle, setExperienceTitle] = useState(title);

  // Edit mode state machine
  const {
    mode,
    data,
    hasUnsavedChanges,
    error: editError,
    enterEditMode,
    cancelEdit,
    updateField,
    saveDraft,
    saveAndPublish
  } = useEditMode(heroData, save);

  const error = saveError || editError || localError;

  // Track title changes separately (title is not part of hero section data)
  const titleChanged = experienceTitle !== title;
  const hasAnyUnsavedChanges = hasUnsavedChanges || titleChanged;

  // Update experience title via API
  const updateExperienceTitle = async (newTitle) => {
    const token = localStorage.getItem('accessToken');
    const headers = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}/api/v1/experiences/${experienceId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ title: newTitle }),
    });

    if (!response.ok) {
      throw new Error('Failed to update experience title');
    }

    return response.json();
  };

  // Handle save draft
  const handleSaveDraft = async () => {
    try {
      setLocalError(null);

      // Save hero section data
      await saveDraft();

      // Update experience title if changed
      if (experienceTitle !== title) {
        await updateExperienceTitle(experienceTitle);
      }

      if (onSectionSaved) {
        onSectionSaved();
      }
    } catch (err) {
      setLocalError(err.message);
    }
  };

  // Handle save and publish
  const handlePublish = async () => {
    try {
      setLocalError(null);

      // Save hero section data
      await saveAndPublish();

      // Update experience title if changed
      if (experienceTitle !== title) {
        await updateExperienceTitle(experienceTitle);
      }

      if (onSectionSaved) {
        onSectionSaved();
      }
    } catch (err) {
      setLocalError(err.message);
    }
  };

  // Render edit mode
  if (mode === 'edit' || mode === 'saving') {
    return (
      <ImageGalleryEdit
        data={data}
        updateField={updateField}
        onCancel={cancelEdit}
        onSaveDraft={handleSaveDraft}
        onPublish={handlePublish}
        isSaving={mode === 'saving'}
        hasUnsavedChanges={hasAnyUnsavedChanges}
        error={error}
        title={experienceTitle}
        onTitleChange={setExperienceTitle}
      />
    );
  }

  // Render view mode
  return (
    <ImageGalleryView
      images={images}
      alt={alt}
      title={title}
      tagline={tagline}
      isGuide={isGuide}
      onEdit={isGuide ? enterEditMode : null}
    />
  );
}
